/**
 * A per-tab identifier, forwarded to the supplier so a guest's quoted price
 * stays put between searching and checking out.
 *
 * The supplier documents `sessionId` on the rates request as keeping prices
 * consistent within a session. Without one, a rate can move between the search
 * a guest accepted and the lock we take a moment later. That movement is
 * already caught — an increase interrupts them for a fresh yes — but every
 * avoidable interruption is a guest asked to re-approve a price they had
 * already agreed to.
 *
 * `sessionStorage`, not `localStorage`: it should last exactly as long as the
 * shopping does, and a value that outlived the tab would pin a guest to a
 * stale session days later.
 *
 * It identifies a BROWSING SESSION, never a person. Random, opaque, never sent
 * anywhere but the supplier, and gone when the tab closes.
 */
const KEY = "mj.stay.sessionId";

export function getStaySessionId(): string | undefined {
  // SSR has no sessionStorage, and a server-rendered page has no session to
  // pin anyway — the first client render supplies one.
  if (typeof window === "undefined") return undefined;
  try {
    const existing = window.sessionStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    window.sessionStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // Private modes and storage-blocking extensions throw on access. Pricing
    // must not depend on it: without a session the supplier behaves exactly as
    // it did before this existed.
    return undefined;
  }
}
